import * as React from "react"
import { Button } from "~/react/ui/button"
import { Input } from "~/react/ui/input"
import { Icon } from "~/react/ui/icon"
import { Label } from "~/react/ui/label"
import { Hint } from "~/react/ui/hint"
import { cn } from "@/lib/utils"

export interface InputGroupProps {
  id: string
  value: string
  placeholder?: string
  error?: string
  isInvalid?: boolean
  isLoading?: boolean
  disabled?: boolean
  buttonText?: string
  loadingText?: string
  label?: string
  showFieldWrapper?: boolean
  className?: string
  inputClassName?: string
  buttonClassName?: string
  inputProps?: React.ComponentProps<typeof Input>
  buttonProps?: React.ComponentProps<typeof Button>
  children?: React.ReactNode
  onChange: (value: string) => void
  onBlur?: () => void
  onSubmit: () => void
}

export const InputGroup = React.forwardRef<HTMLFormElement, InputGroupProps>(
  (
    {
      id,
      value,
      placeholder,
      error,
      isInvalid = false,
      isLoading = false,
      disabled = false,
      buttonText,
      loadingText,
      label,
      showFieldWrapper = false,
      className,
      inputClassName,
      buttonClassName,
      inputProps,
      buttonProps,
      children,
      onChange,
      onBlur,
      onSubmit,
    },
    ref,
  ) => {
    const handleSubmit = (e: React.FormEvent) => {
      e.preventDefault()
      if (!disabled && !isLoading && value.trim() && !isInvalid) {
        onSubmit()
      }
    }

    const hasError = !!error || isInvalid

    const input = (
      <Input
        id={id}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        aria-invalid={hasError}
        disabled={disabled}
        fullWidth={showFieldWrapper}
        className={cn(hasError ? "error" : undefined, "h-input--error", inputClassName)}
        {...inputProps}
      />
    )

    const inputsContent = children ?? input

    const button = (
      <Button
        type="submit"
        size="sm"
        variant="default"
        disabled={!value.trim() || isLoading || disabled || isInvalid}
        aria-busy={isLoading}
        className={buttonClassName}
        {...buttonProps}
      >
        {isLoading ? loadingText : buttonText}
      </Button>
    )

    return (
      <>
        <form ref={ref} onSubmit={handleSubmit} className={className}>
          {label && <Label htmlFor={id}>{label}</Label>}
          <div className="d-flex gap-2 w-100">
            {showFieldWrapper ? (
              <div className="h-field flex-grow-1">
                {inputsContent}
                {error && (
                  <span className="h-field__append">
                    <Icon icon="circle-exclamation" className="text-danger" size="sm" aria-hidden="true" />
                  </span>
                )}
              </div>
            ) : (
              <div className="flex-grow-1">{inputsContent}</div>
            )}
            {button}
          </div>
        </form>
        {hasError && (
          <Hint variant="error" className="mt-1">
            {error}
          </Hint>
        )}
      </>
    )
  },
)

InputGroup.displayName = "InputGroup"
